import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Input,
  Textarea,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SegmentedControl,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useWaapDataLossRule,
  useUpdateWaapDataLossRule,
  useWaapDataLossApiAppOptions,
  useWaapDataLossApiAssetOptions,
  type WaapDlpRule,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './ApiProtectionDataLossPage.module.css';

// Whitelist Metrics (vendor `key`): app confirmed; api best-guess for API assets.
const WL_METRICS = [
  { value: 'app', label: 'Application' },
  { value: 'api', label: 'API' },
];
const WL_MATCHES = [
  { value: '==', label: 'Equal to' },
  { value: '!=', label: 'Not equal to' },
];

const TABS = [
  { slug: 'detail', label: 'Detail' },
  { slug: 'whitelist', label: 'Whitelist' },
  { slug: 'masking', label: 'Data Masking' },
] as const;

// Deep-clone for local editing. Defensive against null arrays: the vendor (and
// Go's nil-slice marshalling) can send `whitelist`/`mask_specified_api` as null.
const clone = (r: WaapDlpRule): WaapDlpRule => ({
  ...r,
  config: {
    ...r.config,
    whitelist: (r.config.whitelist ?? []).map((w) => ({ ...w, value: [...(w.value ?? [])] })),
    mask_specified_api: [...(r.config.mask_specified_api ?? [])],
  },
});

/**
 * Managed Rule editor (Data Loss Prevention). Three tabs: Detail (read-only —
 * managed metadata), Whitelist (APIs/apps excluded from masking) and Data Masking
 * (response masking config). Every Save is a whole-object PUT.
 */
export function ApiProtectionDataLossRulePage() {
  const { serviceId, ruleId } = useParams<{ serviceId: string; ruleId: string }>();
  // Account-level: a representative domain resolves the customer (no select-website gate).
  const { selected } = useWaapWebsite();
  const domainId = selected?.domainId ?? '';
  const navigate = useNavigate();
  const { show } = useToast();
  const listPath = `/services/waap/${serviceId}/api-protection/data-loss`;

  const { data, isLoading } = useWaapDataLossRule(domainId, ruleId);
  const appOpts = useWaapDataLossApiAppOptions(domainId);
  const assetOpts = useWaapDataLossApiAssetOptions(domainId);

  const [rule, setRule] = useState<WaapDlpRule | null>(null);
  useEffect(() => {
    if (data) setRule(clone(data));
  }, [data]);

  const [tab, setTab] = useState<string>('detail');
  const [wl, setWl] = useState<{ key: string; operation: string; value: string }>({ key: 'app', operation: '==', value: '' });
  const [wlEdit, setWlEdit] = useState<number | null>(null);

  const update = useUpdateWaapDataLossRule(domainId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'api_protection_data_loss_rule', 'edit');
      show({ tone: 'success', title: 'Rule saved' });
      navigate(listPath);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });

  if (isLoading || !rule)
    return (
      <Container size="xl">
        <PageHeader title="Managed Rule" />
        <Skeleton height={320} />
      </Container>
    );


  const cfg = rule.config;
  const patchConfig = (patch: Partial<WaapDlpRule['config']>) =>
    setRule((r) => (r ? { ...r, config: { ...r.config, ...patch } } : r));

  const valueOptions = wl.key === 'app' ? appOpts.data ?? [] : assetOpts.data ?? [];
  const optLabel = (key: string, v: string) => {
    const pool = key === 'app' ? appOpts.data ?? [] : assetOpts.data ?? [];
    return pool.find((o) => o.value === v)?.label ?? v;
  };
  // Add a new whitelist entry, or replace the one being edited (wlEdit).
  const submitWhitelist = () => {
    if (!wl.value) return;
    const entry = { key: wl.key, operation: wl.operation, value: [wl.value] };
    patchConfig({
      whitelist:
        wlEdit === null
          ? [...cfg.whitelist, entry]
          : cfg.whitelist.map((w, i) => (i === wlEdit ? entry : w)),
    });
    setWl({ key: 'app', operation: '==', value: '' });
    setWlEdit(null);
  };
  const startEditWhitelist = (i: number) => {
    const w = cfg.whitelist[i];
    setWl({ key: w.key, operation: w.operation, value: w.value[0] ?? '' });
    setWlEdit(i);
  };
  const removeWhitelist = (i: number) => {
    patchConfig({ whitelist: cfg.whitelist.filter((_, idx) => idx !== i) });
    if (wlEdit === i) {
      setWl({ key: 'app', operation: '==', value: '' });
      setWlEdit(null);
    }
  };

  const toggleSpecifiedApi = (v: string, on: boolean) =>
    patchConfig({ mask_specified_api: on ? [...cfg.mask_specified_api, v] : cfg.mask_specified_api.filter((x) => x !== v) });

  const save = () => update.mutate({ id: rule.id, body: rule });

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'API Protection' },
              { label: 'Data Loss Prevention', slug: 'api-protection/data-loss' },
              { label: 'Managed Rule' },
            ]}
          />
        }
        title={rule.name}
      />

      <Tabs value={tab} onValueChange={setTab} style={{ marginBottom: 'var(--vn-space-4)' }}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.slug} value={t.slug}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
        {tab === 'detail' ? (
          <Card flat>
            <CardHeader>
              <CardTitle>Detail</CardTitle>
              <CardDescription>Managed rule metadata (read-only).</CardDescription>
            </CardHeader>
            <CardBody>
              <div className={styles.form}>
                <Field label="Name">{(p) => <Input {...p} value={rule.name} disabled />}</Field>
                <Field label="Description">{(p) => <Textarea {...p} value={cfg.description} disabled />}</Field>
                <Field label="Risk Level">{(p) => <Input {...p} value={cfg.pii_level} disabled />}</Field>
                <Field label="Target">
                  {() => (
                    <div className={styles.checkRow}>
                      <label className={styles.checkLabel}>
                        <Checkbox checked={cfg.req_enabled} disabled /> Request
                      </label>
                      <label className={styles.checkLabel}>
                        <Checkbox checked={cfg.is_pii_masked} disabled /> Response
                      </label>
                    </div>
                  )}
                </Field>
              </div>
            </CardBody>
          </Card>
        ) : tab === 'whitelist' ? (
          <Card flat>
            <CardHeader>
              <CardTitle>Whitelist</CardTitle>
              <CardDescription>Configure the metric, matching condition and value of the APIs to add to the whitelist.</CardDescription>
            </CardHeader>
            <CardBody>
              <div className={styles.form}>
                <div className={styles.condRow}>
                  <Select value={wl.key} onValueChange={(v) => setWl((c) => ({ ...c, key: v, value: '' }))}>
                    <SelectTrigger className={styles.condSelect}><SelectValue /></SelectTrigger>
                    <SelectContent>{WL_METRICS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={wl.operation} onValueChange={(v) => setWl((c) => ({ ...c, operation: v }))}>
                    <SelectTrigger className={styles.condSelect}><SelectValue /></SelectTrigger>
                    <SelectContent>{WL_MATCHES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={wl.value} onValueChange={(v) => setWl((c) => ({ ...c, value: v }))}>
                    <SelectTrigger className={styles.condValue}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{valueOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="secondary" size="sm" onClick={submitWhitelist} disabled={!wl.value}>
                    {wlEdit === null ? 'Add' : 'Update'}
                  </Button>
                </div>

                {cfg.whitelist.length > 0 && (
                  <div className={styles.condList}>
                    <div className={styles.condHead}>
                      <span>Metrics</span>
                      <span>Matching Condition</span>
                      <span>Value</span>
                      <span>Operation</span>
                    </div>
                    {cfg.whitelist.map((w, i) => (
                      <div key={i} className={styles.condItem}>
                        <span>{WL_METRICS.find((m) => m.value === w.key)?.label ?? w.key}</span>
                        <span className={styles.muted}>{WL_MATCHES.find((m) => m.value === w.operation)?.label ?? w.operation}</span>
                        <span>{w.value.map((v) => optLabel(w.key, v)).join(', ')}</span>
                        <div className={styles.actions}>
                          <Button variant="ghost" size="sm" onClick={() => startEditWhitelist(i)}>Edit</Button>
                          <Button variant="ghost" size="sm" onClick={() => removeWhitelist(i)}>Delete</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        ) : (
          <Card flat>
            <CardHeader>
              <CardTitle>Data Masking</CardTitle>
              <CardDescription>{'Response Data Masking — mask the first X and last Y characters with "*".'}</CardDescription>
            </CardHeader>
            <CardBody>
              <div className={styles.form}>
                <label className={styles.checkLabel}>
                  <Checkbox checked={cfg.is_pii_masked} onCheckedChange={(v) => patchConfig({ is_pii_masked: Boolean(v) })} />
                  Enable
                </label>
                <Field label="First X number of characters">
                  {(p) => <Input {...p} className={styles.maskField} type="number" min={0} value={String(cfg.pre_n)} onChange={(e) => patchConfig({ pre_n: Number(e.target.value) || 0 })} />}
                </Field>
                <Field label="Last Y number of characters">
                  {(p) => <Input {...p} className={styles.maskField} type="number" min={0} value={String(cfg.last_m)} onChange={(e) => patchConfig({ last_m: Number(e.target.value) || 0 })} />}
                </Field>
                <Field label="Apply Rule to">
                  {() => (
                    <SegmentedControl
                      items={[
                        { value: 'all', label: 'All APIs' },
                        { value: 'selected', label: 'Selected APIs' },
                      ]}
                      value={cfg.mask_all_api ? 'all' : 'selected'}
                      onValueChange={(v) => patchConfig({ mask_all_api: v === 'all' })}
                      aria-label="Apply rule to"
                    />
                  )}
                </Field>
                {!cfg.mask_all_api &&
                  (assetOpts.data && assetOpts.data.length > 0 ? (
                    <div className={styles.checkRow}>
                      {assetOpts.data.map((o) => (
                        <label key={o.value} className={styles.checkLabel}>
                          <Checkbox
                            checked={cfg.mask_specified_api.includes(o.value)}
                            onCheckedChange={(v) => toggleSpecifiedApi(o.value, Boolean(v))}
                          />
                          {o.label}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <span className={styles.empty}>No API assets available.</span>
                  ))}
              </div>
            </CardBody>
          </Card>
        )}

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={() => navigate(listPath)}>Cancel</Button>
          <Button disabled={update.isPending} onClick={save}>{update.isPending ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
    </Container>
  );
}
